import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import axios from 'axios';

export default function LoginPage() {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState({
    app_naam: 'BillFac',
    logo_url: '',
    primary_kleur: '#002FA7',
    bedrijf_naam: '',
  });

  useEffect(() => {
    // Publieke branding ophalen (geen auth vereist)
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${apiBase}/api/branding/public`).then(r => setBranding(r.data)).catch(() => {});
    // Veiligheid: zorg dat er GEEN oude Service Worker actief is op de login-pagina.
    // Een oude SW kan auth-requests onderscheppen en "Geen verbinding"-fouten veroorzaken
    // na een deploy. We deregisteren alle SWs en wissen runtime-caches zonder reload,
    // zodat de huidige login-poging meteen door de echte server gaat.
    (async () => {
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          if (regs.length > 0) {
            await Promise.all(regs.map(r => r.unregister()));
          }
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          const oude = keys.filter(k => !k.startsWith('emergent-billing-v3'));
          await Promise.all(oude.map(k => caches.delete(k)));
        }
      } catch { /* stil falen — niet-kritiek */ }
    })();
  }, []);

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Vul alle velden in');
      return;
    }
    setLoading(true);
    setError('');
    const result = await login(form.email, form.password);
    if (!result.ok) {
      setError(result.error);
    }
    setLoading(false);
  };

  // "App resetten" — wist Service Worker + caches + localStorage en laadt opnieuw.
  // Praktische noodknop wanneer gebruiker vast zit in "Er ging iets mis"/netwerkfout-loops.
  const [resetBusy, setResetBusy] = useState(false);
  const handleReset = async () => {
    if (!window.confirm('App volledig resetten? Dit wist de lokale cache en herlaadt de pagina. Je inloggegevens zijn NIET gewijzigd.')) return;
    setResetBusy(true);
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      try { localStorage.clear(); sessionStorage.clear(); } catch { /* quota */ }
    } catch { /* negeren */ }
    window.location.reload();
  };

  // Toon reset-knop wanneer de laatste fout netwerk-gerelateerd is
  const toonResetKnop = error && /geen (respons|verbinding)|time-?out|netwerk|server-fout/i.test(error);

  // Splits app_naam: eerste woord krijgt accent-kleur, rest blijft zwart
  const naamDelen = (branding.app_naam || 'BillFac').split(' ');
  const eerste = naamDelen[0];
  const rest = naamDelen.slice(1).join(' ');

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {branding.logo_url ? (
            <img
              src={branding.logo_url.startsWith('http') ? branding.logo_url : `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`}
              alt={branding.app_naam}
              style={{ maxHeight: 60, margin: '0 auto 12px', objectFit: 'contain' }}
            />
          ) : branding.app_naam === 'BillFac' ? (
            <img
              src="/logo.svg"
              alt="BillFac"
              style={{ height: 80, width: 80, margin: '0 auto 16px', filter: 'drop-shadow(0 8px 24px rgba(0,47,167,0.25))' }}
            />
          ) : null}
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
            {branding.app_naam === 'BillFac' ? (
              <>
                <span style={{ color: branding.primary_kleur }}>Bill</span>
                <span className="text-gray-900">Fac</span>
              </>
            ) : (
              <>
                <span style={{ color: branding.primary_kleur }}>{eerste}</span>
                {rest && <> {rest}</>}
              </>
            )}
          </h1>
          <p className="text-gray-500 mt-2">Inloggen op uw account</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="form-label">E-mailadres</label>
                <input
                  type="email"
                  className="field-input"
                  value={form.email}
                  onChange={u('email')}
                  placeholder="jij@bedrijf.be"
                  data-testid="login-email"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="form-label">Wachtwoord</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="field-input pr-10"
                    value={form.password}
                    onChange={u('password')}
                    placeholder="••••••••"
                    data-testid="login-password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm" data-testid="login-error">
                <div>{error}</div>
                {toonResetKnop && (
                  <div className="mt-2 pt-2 border-t border-red-200">
                    <p className="text-[11px] text-red-800 mb-1.5">
                      Blijft dit foutbericht terugkomen? Reset de lokale app-cache — dit lost vaak problemen op met een oude versie of vastzittende Service Worker.
                    </p>
                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={resetBusy}
                      className="text-[11px] font-bold px-2.5 py-1 bg-white border border-red-300 text-red-700 hover:bg-red-100 transition-colors rounded"
                      data-testid="login-reset-cache"
                    >
                      {resetBusy ? 'Resetten...' : '↻ App-cache wissen & herladen'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary w-full mt-6 justify-center"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Inloggen...' : 'Inloggen'}
            </button>

            {/* Wachtwoord-vergeten link */}
            <div className="text-center mt-3">
              <Link to="/wachtwoord-vergeten" className="text-xs text-[#002FA7] hover:underline" data-testid="login-forgot-pw">
                Wachtwoord vergeten?
              </Link>
            </div>

            {/* Inloggen met Google — Emergent-managed OAuth.
                REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div>
              <div className="relative flex justify-center text-[11px]"><span className="px-2 bg-white text-gray-400 uppercase tracking-wider">of</span></div>
            </div>
            <button
              type="button"
              className="w-full py-2.5 px-4 border border-gray-300 hover:border-gray-400 bg-white text-gray-700 font-medium text-sm flex items-center justify-center gap-2.5 transition-colors"
              onClick={() => {
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="login-google"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382c-.232 1.25-.937 2.31-1.997 3.018v2.51h3.232c1.891-1.74 2.983-4.305 2.983-7.351z" fill="#4285F4"/><path d="M12 22c2.7 0 4.964-.895 6.618-2.422l-3.232-2.51c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H3.064v2.59C4.71 19.736 8.09 22 12 22z" fill="#34A853"/><path d="M6.405 13.9c-.2-.6-.314-1.241-.314-1.9s.114-1.3.314-1.9V7.51H3.064C2.387 8.857 2 10.381 2 12s.387 3.143 1.064 4.49l3.341-2.59z" fill="#FBBC05"/><path d="M12 5.977c1.47 0 2.786.505 3.823 1.496l2.868-2.868C16.96 2.99 14.696 2 12 2 8.09 2 4.71 4.264 3.064 7.51l3.341 2.59C7.19 7.737 9.395 5.977 12 5.977z" fill="#EA4335"/></svg>
              Inloggen met Google
            </button>
          </form>

          <div className="text-center mt-4 pt-4 border-t border-gray-100 text-xs text-gray-600">
            Nog geen account?{' '}
            <Link to="/register" className="text-[#002FA7] font-semibold hover:underline" data-testid="login-naar-register">
              Start gratis 14-daagse proefperiode
            </Link>
          </div>
        </div>

        <div className="text-center mt-6 text-xs text-gray-400">
          © {new Date().getFullYear()} {branding.bedrijf_naam || branding.app_naam}. Alle rechten voorbehouden.
        </div>
      </div>
    </div>
  );
}
